import { useCallback, useEffect, useState } from "react";
import { FormAlert } from "@NP-Webber/crm-kit";
import { shellApi } from "../../api/shellApi";
import { mailingApi, type CustomerMailingDto } from "../../api/mailingApi";
import { toClientApiError } from "../../api/httpClient";

export type CustomerMailHistoryPanelProps = {
  customerId: string;
};

const STATUS_LABELS: Record<string, string> = {
  sent: "נשלח",
  failed: "נכשל",
  queued: "בתור",
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/** one row of the history: a single email (mail log) or a mailing the customer was in */
type HistoryRow = { id: string; subject: string; at: string; email: string | null; status: string; mailing: boolean };

function mailingStatus(m: CustomerMailingDto): string {
  if (m.unsubscribedAt) return "הוסר מהדיוור";
  if (m.clickedAt) return "הקליק";
  if (m.openedAt) return "נפתח";
  if (m.status === "bounced") return "חזר";
  if (m.status === "failed") return "נכשל";
  if (m.status === "queued" || m.status === "held") return "בתור";
  return "נשלח";
}

function formatSentAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("he-IL");
}

export function CustomerMailHistoryPanel({
  customerId,
}: CustomerMailHistoryPanelProps) {
  const [items, setItems] = useState<HistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [log, mailings] = await Promise.all([
        shellApi.listMailLog({
          relatedEntityType: "customer",
          relatedEntityId: customerId,
        }),
        // mailings (ADR-0033) — empty without mailing.view
        mailingApi.customerHistory(customerId).catch(() => [] as CustomerMailingDto[]),
      ]);
      const rows: HistoryRow[] = [
        ...log.map((m) => ({ id: m.id, subject: m.subject, at: m.sentAt, email: m.recipientEmail, status: statusLabel(m.status), mailing: false })),
        ...mailings.map((m) => ({
          id: m.id,
          subject: m.subject || m.blastName,
          at: m.sentAt ?? "",
          email: m.email,
          status: mailingStatus(m),
          mailing: true,
        })),
      ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
      setItems(rows);
    } catch (err) {
      setError(toClientApiError(err).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="related-panel__empty">טוען היסטוריית דיוור…</p>;
  }

  if (error) {
    return <FormAlert severity="error">{error}</FormAlert>;
  }

  if (items.length === 0) {
    return <p className="related-panel__empty">אין רשומות דיוור לקופה זו.</p>;
  }

  return (
    <div className="related-panel" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((m) => (
        <div
          key={m.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 14px",
            borderRadius: 8,
            background: "var(--crm-surface-muted, #fafaf8)",
            border: "1px solid var(--ck-border)",
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{m.subject}</div>
            <div style={{ fontSize: 11, color: "var(--ck-text-3)" }}>
              {m.mailing ? "דיוור · " : ""}
              {m.at ? formatSentAt(m.at) : "ממתין לשליחה"}
              {m.email ? ` · ${m.email}` : ""}
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{m.status}</span>
        </div>
      ))}
    </div>
  );
}
